import classNames from "classnames";
import { useTranslations } from "next-intl";
import Image from "next/image";
import React, { FC, useEffect, useRef, useState } from "react";
import SwiperCore from "swiper";
// import "swiper/css";
// import "swiper/css/autoplay";
// import "swiper/css/navigation";
// import "swiper/css/pagination";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";

import { TileCarouselCardProps, TileCarouselModalProps } from "@/types";

import { CompactModal } from "../CompactModal/CompactModal";
import RichText from "../RichText/RichText";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "../ui/Button/Button";
import Icon from "../ui/Icon/Icon";
import Modal from "../ui/Modal/Modal";

interface Props {
  card: TileCarouselCardProps;
  onClose: () => void;
}

SwiperCore.use([Autoplay, Pagination, Navigation]);

const TileCarouselContent: FC<Props> = ({ card, onClose }) => {
  const t = useTranslations();
  const arrowLeft = useRef<HTMLButtonElement>(null);
  const arrowRight = useRef<HTMLButtonElement>(null);
  const slider = useRef<SwiperRef>(null);

  if (card.modal?.layout === "full-screen") {
    return (
      <>
        <div className="flex justify-between items-center p-l">
          <span className="text-m font-secondary uppercase">{card.title}</span>
          <button
            onClick={() => {
              onClose();
            }}
          >
            <Icon className="stroke-black" name="close" size="24" title={t("button.label.closeModal")} />
          </button>
        </div>
        <div className="px-m mb-xl md:mx-auto md:max-w-5xl md:mb-l">
          <div className="flex flex-col md:flex-row md:items-center mt-xl lg:mt-0">
            {card.logo && (
              <Image
                className="mb-xxl md:mb-0 filter invert"
                src={card.logo?.url}
                alt={card.logo?.title || ""}
                width={card.logo?.width}
                height={card.logo?.height}
              />
            )}
            <div>
              <div className="text-left md:pl-l">
                <h3 className="font-secondary uppercase text-s md:mb-s mb-xs">{card.modal?.descriptionTitle}</h3>
                <p className="text-m font-secondary-light">
                  <RichText content={{ html: card.modal?.description?.html || "" }} />
                </p>
              </div>
            </div>
            {card.modal?.cta && (
              <Button
                customClass="block mt-xl mb-l w-full md:hidden text-center"
                size={ButtonSize.LARGE}
                theme={ButtonTheme.BLACK}
                type="button"
                variant={ButtonVariant.SECONDARY}
              >
                {card.modal?.cta?.label}
              </Button>
            )}
          </div>
        </div>
        <div className="overflow-auto px-m md:hidden">
          {card.modal?.gallery?.map((item, index) => (
            <Image
              key={index}
              className="w-full h-auto rounded-lg mb-l"
              src={item.url}
              alt={item.title || ""}
              width={item.width}
              height={item.height}
            />
          ))}
        </div>
        <div className="hidden md:flex flex-col">
          <Swiper
            ref={slider}
            slidesPerView={1.6}
            spaceBetween={24}
            height={200}
            centeredSlides={true}
            loop={true}
            initialSlide={1}
            enabled={true}
            breakpoints={{
              1900: {
                slidesPerView: 1.9,
              },
            }}
            pagination={{ type: "bullets", el: ".swiper-pagination-modal", clickable: true }}
            className="w-full relative"
          >
            {card.modal?.gallery?.map((item, index) => (
              <SwiperSlide className="" key={index}>
                <Image
                  className="rounded-lg h-full w-auto mx-auto object-cover"
                  src={item.url}
                  alt={item.title || ""}
                  width={item.width}
                  height={item.height}
                />
              </SwiperSlide>
            ))}
            <button
              ref={arrowLeft}
              onClick={() => {
                slider?.current?.swiper?.slidePrev();
              }}
              className="absolute left-[17.7%] md:left-[17.5%] top-[45%] z-10 bg-gray-50 w-[44px] aspect-1-1 rounded-full flex items-center justify-center"
            >
              <Icon className="stroke-white" name="arrow-left" size="20" />
            </button>
            <button
              ref={arrowRight}
              onClick={() => {
                slider?.current?.swiper?.slideNext();
              }}
              className="absolute left-[79.3%] md:left-[79.5%] top-[45%] z-10 bg-gray-50 w-[44px] aspect-1-1 rounded-full flex items-center justify-center"
            >
              <Icon className="stroke-white" name="arrow-right" size="20" />
            </button>
          </Swiper>

          <div className="">
            <div className="custom-modal-swiper swiper-pagination-modal mt-m"></div>
            {card.modal?.cta && (
              <Button
                href={card.modal?.cta?.link.url}
                customClass="block w-fit mt-l mx-auto"
                size={ButtonSize.LARGE}
                theme={ButtonTheme.BLACK}
                type="button"
                variant={ButtonVariant.SECONDARY}
              >
                {card.modal?.cta?.label}
              </Button>
            )}
          </div>
        </div>
      </>
    );
  } else if (card.modal?.layout === "compact") {
    return (
      <CompactModal
        title={card.title}
        image={card.modal.gallery[0]}
        bodyTitle={card.modal.descriptionTitle || ""}
        bodySubtitle={card.modal.subtitle}
        bodyText={card.modal.description?.html || ""}
        onClose={onClose}
      ></CompactModal>
    );
  }
};

export default TileCarouselContent;
